import {FC, useState, useEffect, ChangeEvent} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {POST, PUT, GET} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import uuid from 'react-uuid'
import {HotelExperienceType, getTypeLabel, UpdateHotelExperienceDto} from './types'
import {HospitalityTabs} from '../../../components/HospitalityTabs/HospitalityTabs'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {TitleFreePriceInput} from '../Comuns'
import {AdditionalServicesTab} from './AdditionalServicesTab'

const UpdateHotelExperienceContent: FC = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('basic')
  const {id} = useParams<{id: string}>()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    titleFreePrice: '',
    duration: '',
    includes: [''],
    type: HotelExperienceType.ROMANCE,
    rating: '',
    order: 0,
    status: true,
    content: [] as string[],
    showAdditionalsServices: false,
    entertainments: [] as {entertainmentServiceId: number; price: number}[],
  })
  const [image, setImage] = useState<File | null | string>(null)
  const [currentImage, setCurrentImage] = useState<string>('')

  // Estados para gerenciar serviços de entretenimento
  const [searchService, setSearchService] = useState('')
  const [availableServices, setAvailableServices] = useState<any[]>([])
  const [loadingServices, setLoadingServices] = useState(false)
  const [selectedServices, setSelectedServices] = useState<any[]>([])

  // Carregar dados da experiência
  useEffect(() => {
    const getExperience = async () => {
      try {
        setLoading(true)
        const res = await GET(`/hotel-experiences/${id}`)
        const experience = res.data

        setFormData({
          title: experience.title || '',
          description: experience.description || '',
          price: experience.price?.toString() || '',
          titleFreePrice: experience.titleFreePrice || '',
          duration: experience.duration || '',
          includes: experience.includes || [''],
          type: experience.type || HotelExperienceType.ROMANCE,
          rating: experience.rating?.toString() || '',
          status: experience.status !== false,
          order: experience.order || 0,
          content: experience.content || [],
          showAdditionalsServices: experience.showAdditionalsServices || false,
          entertainments: experience.entertainments || [],
        })
        setCurrentImage(experience.image || '')
        setImage(experience.image || null)

        // Carregar serviços selecionados
        if (experience.entertainments && experience.entertainments.length > 0) {
          const servicesResponse = await POST('/hotel-experience-entertainment/filter', {
            hotelExperiencesId: id,
          })

          if (servicesResponse.success && servicesResponse.data) {
            const loadedServices = servicesResponse.data.map((service: any) => {
              const entertainmentData = experience.entertainments.find(
                (e: any) => e.id === service.id
              )
              return {
                ...service,
                customPrice: entertainmentData?.price || service.price || 0,
              }
            })
            // Deduplicate by id/entertainmentServiceId/title
            const unique: any[] = []
            const seen = new Set<string>()
            for (const s of loadedServices) {
              const sid = s.id ?? s.entertainmentServiceId ?? ''
              const key = String(sid) || (s.title ? `t:${s.title}` : '')
              if (!seen.has(key)) {
                seen.add(key)
                unique.push(s)
              }
            }
            setSelectedServices(unique)
          }
        }

        setLoading(false)
      } catch (e) {
        setLoading(false)
        console.log(e)
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_experiences.msg_error_load')),
          text: String(i18n.t('hospitality_experiences.msg_error_load_text')),
        }).then(() => navigate('/hospitality/hotel-experiences'))
      }
    }

    if (id) {
      getExperience()
    }
  }, [id, navigate])

  const handleInputChange = (
    field: string,
    value: string | HotelExperienceType | boolean | number
  ) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      } as any

      // Se o campo alterado for 'price', limpar titleFreePrice quando preço for maior que 0
      if (field === 'price') {
        const num = Number(String(value))
        if (Number.isFinite(num) && num > 0) {
          updated.titleFreePrice = ''
        }
      }

      return updated
    })
  }

  const handleIncludesChange = (index: number, value: string) => {
    const newIncludes = [...formData.includes]
    newIncludes[index] = value
    setFormData({
      ...formData,
      includes: newIncludes,
    })
  }

  const addIncludeItem = () => {
    setFormData({
      ...formData,
      includes: [...formData.includes, ''],
    })
  }

  const removeIncludeItem = (index: number) => {
    if (formData.includes.length > 0) {
      const newIncludes = formData.includes.filter((_, i) => i !== index)
      setFormData({
        ...formData,
        includes: newIncludes,
      })
    }
  }

  const handlePeriodChange = (period: string) => {
    const newPeriods = formData.content.includes(period)
      ? formData.content.filter((p) => p !== period)
      : [...formData.content, period]
    setFormData({
      ...formData,
      content: newPeriods,
    })
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_title')),
      })
      return false
    }

    if (formData.title.length > 255) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_title_length')),
      })
      return false
    }

    if (!formData.description.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_description')),
      })
      return false
    }

    if (!formData.price.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_price')),
      })
      return false
    }

    if (parseFloat(formData.price) < 0) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_price_negative')),
      })
      return false
    }

    if (!formData.duration.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_duration')),
      })
      return false
    }

    if (formData.duration.length > 100) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_duration_length')),
      })
      return false
    }

    // const nonEmptyIncludes = formData.includes.filter((item) => item.trim())
    // if (nonEmptyIncludes.length === 0) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: String(i18n.t('hospitality_experiences.msg_error_update')),
    //     text: String(i18n.t('hospitality_experiences.msg_error_required_includes')),
    //   })
    //   return false
    // }

    if (formData.rating && (parseFloat(formData.rating) < 0 || parseFloat(formData.rating) > 5)) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_rating_range')),
      })
      return false
    }

    if (
      formData.order === null ||
      formData.order === undefined ||
      String(formData.order).trim() === '' ||
      Number(formData.order) < 0
    ) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_experiences.msg_error_create')),
        text: String(i18n.t('hospitality_experiences.msg_error_order_negative')),
      })
      return false
    }

    return true
  }

  // Funções para gerenciar serviços de entretenimento
  const searchEntertainmentServices = async () => {
    try {
      setLoadingServices(true)
      const response = await POST('/entertainment-service/filter', {
        title: searchService,
        status: true,
        type: 'entertainment_experience',
      })

      if (response.success && response.data) {
        setAvailableServices(response.data)
      } else {
        setAvailableServices([])
      }
      setLoadingServices(false)
    } catch (error) {
      setLoadingServices(false)
      console.error('Erro ao buscar serviços:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao buscar serviços de entretenimento',
      })
    }
  }

  const addServiceToExperience = (service: any) => {
    const alreadyAdded = selectedServices.some((s) => {
      const sid = s.id ?? s.entertainmentServiceId
      const aid = service.id ?? service.entertainmentServiceId
      if (String(sid) === String(aid)) return true
      if (s.title && service.title && s.title === service.title) return true
      if (s.name && service.name && s.name === service.name) return true
      return false
    })
    if (alreadyAdded) {
      Swal.fire({
        icon: 'info',
        title: 'Atenção',
        text: 'Este serviço já foi adicionado',
      })
      return
    }

    setSelectedServices([...selectedServices, {...service, customPrice: service.price || 0}])
  }

  const removeServiceFromExperience = (serviceId: number) => {
    setSelectedServices(selectedServices.filter((s) => s.id !== serviceId))
  }

  const updateServicePrice = (serviceId: number, price: number) => {
    setSelectedServices(
      selectedServices.map((s) => (s.id === serviceId ? {...s, customPrice: price} : s))
    )
  }

  // Sincronizar selectedServices com formData.entertainments
  useEffect(() => {
    const entertainments = selectedServices.map((s) => ({
      entertainmentServiceId: s.entertainmentServiceId ?? s.id,
      price: s.customPrice || 0,
    }))
    setFormData((prev) => ({...prev, entertainments}))
  }, [selectedServices])

  const updateExperience = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      let imageRes = {
        success: false,
        image: currentImage,
      }

      if (image !== currentImage && image instanceof File) {
        if (currentImage) {
          await POST('/upload-images/delete-one', {
            url: currentImage,
          })
        }

        const formData = new FormData()
        formData.append('file', image)
        formData.append('nameImage', `publicity-${uuid()}.jpg`)
        formData.append('originalSize', 'true')
        imageRes = await POST('/upload-images/upload-one', formData)
      }

      const payload: UpdateHotelExperienceDto = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        titleFreePrice: formData.titleFreePrice || null,
        duration: formData.duration.trim(),
        order: formData.order,
        includes: formData.includes.filter((item) => item.trim()),
        type: formData.type,
        rating: formData.rating ? parseFloat(formData.rating) : undefined,
        image: imageRes.success ? imageRes.image : typeof image === 'string' ? image : undefined,
        status: formData.status,
        content: formData.content,
        showAdditionalsServices: formData.showAdditionalsServices,
        entertainments: formData.entertainments.length > 0 ? formData.entertainments : [],
      }

      const response = await PUT(`/hotel-experiences/${id}`, payload)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_experiences.btn_update')),
          text: String(i18n.t('hospitality_experiences.msg_success_update')),
        }).then(() => {
          navigate('/hospitality/hotel-experiences')
        })
      } else {
        throw new Error(response.message || i18n.t('hospitality_experiences.msg_error_update'))
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(e.message || i18n.t('hospitality_experiences.msg_error_update')),
      })
      console.log(e)
    }
  }

  const renderBasicTab = () => (
    <>
      <div className='row'>
        <div className='col-lg-8'>
          <div className='col-lg-12'>
            <Input
              label={i18n.t('hospitality_experiences.field_title')}
              inputAttr={{
                type: 'text',
                placeholder: i18n.t('hospitality_experiences.field_title_placeholder'),
                maxLength: 255,
              }}
              name='title'
              value={formData.title}
              change={(value: string) => handleInputChange('title', value)}
            />
          </div>
          <div className='col-lg-12'>
            <EditorQuill
              value={formData.description}
              label={i18n.t('hospitality_experiences.field_description')}
              placeholder={i18n.t('hospitality_experiences.field_description_placeholder')}
              change={(value: string) => handleInputChange('description', value)}
              maxLength={5000} // Limite de 5000 caracteres
            />
          </div>
        </div>

        <div className='col-lg-4'>
          <Input
            label={i18n.t('hospitality_experiences.field_image')}
            inputAttr={{
              type: 'image',
            }}
            name='image'
            value={image}
            change={(value: React.BaseSyntheticEvent) => setImage(value.target.files[0])}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-lg-4'>
          <TitleFreePriceInput
            value={formData.titleFreePrice || ''}
            onChange={(value) => handleInputChange('titleFreePrice', value || '')}
            label='Título atividade sem custo'
            placeholder='Ex:. Cortesia do Hotel'
            maxCaracteres={30}
            disabled={parseFloat(formData.price) !== 0 && formData.price !== ''}
            toolTip={true}
            toolTiptext='Se o preço estiver zerado, este texto poderá ser exibido no WebApp. Se vazio, nada será mostrado.'
            toolTipIcon='info-circle'
          />
        </div>
        <div className='col-lg-4'>
          <Input
            label={i18n.t('hospitality_experiences.field_price')}
            inputAttr={{
              type: 'number',
              placeholder: i18n.t('hospitality_experiences.field_price_placeholder'),
              step: '0.01',
              min: '0',
            }}
            name='price'
            value={formData.price}
            change={(value: string) => handleInputChange('price', value)}
          />
        </div>

        <div className='col-lg-4'>
          <Input
            label={i18n.t('hospitality_experiences.field_duration')}
            inputAttr={{
              type: 'text',
              placeholder: i18n.t('hospitality_experiences.field_duration_placeholder'),
              maxLength: 100,
            }}
            name='duration'
            value={formData.duration}
            change={(value: string) => handleInputChange('duration', value)}
          />
        </div>
        <div className='row'>
          {' '}
          {/* Campo de Tipo */}
          <div className='col-lg-4'>
            <div className='mb-3 mt-6'>
              <label className='form-label'>{i18n.t('hospitality_experiences.field_type')}</label>
              <select
                className='form-select'
                value={formData.type}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  handleInputChange('type', e.target.value as HotelExperienceType)
                }
              >
                {Object.values(HotelExperienceType).map((type) => (
                  <option key={type} value={type}>
                    {getTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Campo de Ordem */}
          <div className='col-lg-4'>
            <div className='mb-3 mt-6'>
              <Input
                inputAttr={{
                  type: 'number',
                }}
                label={i18n.t('hospitality_experiences.field_order')}
                placeholder={i18n.t('hospitality_experiences.field_order_placeholder')}
                value={formData.order}
                change={(value: string) => handleInputChange('order', value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='row d-flex align-items-center'>
        <div className='col-lg-6'>
          <Input
            label={i18n.t('hospitality_experiences.field_rating')}
            inputAttr={{
              type: 'number',
              placeholder: i18n.t('hospitality_experiences.field_rating_placeholder'),
              step: '0.1',
              min: '0',
              max: '5',
            }}
            name='rating'
            value={formData.rating}
            change={(value: string) => handleInputChange('rating', value)}
          />
        </div>
        <div className='col-lg-6'>
          <Input
            inputAttr={{
              type: 'checkbox',
            }}
            label={i18n.t('hospitality_experiences.field_status')}
            name='status'
            checked={formData.status}
            change={() => handleInputChange('status', !formData.status)}
          />
        </div>
      </div>

      <div className='row mt-6'>
        <div className='col-lg-12'>
          <label className='form-label fw-bold fs-6'>Período de Disponibilidade</label>
          <p className='text-muted mb-3 fs-7'>
            Selecione os períodos em que esta experiência estará disponível:
          </p>
          <div className='d-flex gap-5'>
            <Input
              inputAttr={{
                type: 'checkbox',
              }}
              label='Manhã'
              name='period-morning'
              checked={formData.content.includes('morning')}
              change={() => handlePeriodChange('morning')}
              marginTop='0'
            />
            <Input
              inputAttr={{
                type: 'checkbox',
              }}
              label='Tarde'
              name='period-afternoon'
              checked={formData.content.includes('afternoon')}
              change={() => handlePeriodChange('afternoon')}
              marginTop='0'
            />
            <Input
              inputAttr={{
                type: 'checkbox',
              }}
              label='Noite'
              name='period-night'
              checked={formData.content.includes('night')}
              change={() => handlePeriodChange('night')}
              marginTop='0'
            />
          </div>
        </div>
      </div>
    </>
  )

  const renderDetailsTab = () => (
    <div className='row'>
      <div className='col-lg-12'>
        <label className='form-label'>{i18n.t('hospitality_experiences.field_includes')}</label>
        {formData.includes.map((include, index) => (
          <div key={index} className='d-flex align-items-center mb-2'>
            <Input
              inputAttr={{
                type: 'text',
                placeholder: i18n.t('hospitality_experiences.field_includes_placeholder'),
              }}
              name={`include-${index}`}
              value={include}
              change={(value: string) => handleIncludesChange(index, value)}
            />
            <Button
              text={i18n.t('hospitality_experiences.btn_remove')}
              color='danger'
              size='small'
              click={() => removeIncludeItem(index)}
              btnClass='ms-2 mt-6'
            />
          </div>
        ))}
        <div className='mt-6'>
          <Button
            text={i18n.t('hospitality_experiences.btn_add_item')}
            color='info'
            size='small'
            click={addIncludeItem}
            icon='fas fa-plus'
          />
        </div>
      </div>
    </div>
  )

  const renderAdditionalServicesTab = () => (
    <div className='row'>
      <div className='col-lg-12 mb-6'>
        <Input
          inputAttr={{
            type: 'checkbox',
          }}
          label={i18n.t('hospitality_experiences.field_show_additional_services')}
          name='showAdditionalsServices'
          checked={formData.showAdditionalsServices}
          change={() =>
            handleInputChange('showAdditionalsServices', !formData.showAdditionalsServices)
          }
        />
      </div>
      <div className='col-lg-12 mb-6'>
        <AdditionalServicesTab
          searchService={searchService}
          setSearchService={setSearchService}
          availableServices={availableServices}
          loadingServices={loadingServices}
          selectedServices={selectedServices}
          searchEntertainmentServices={searchEntertainmentServices}
          addServiceToExperience={addServiceToExperience}
          removeServiceFromExperience={removeServiceFromExperience}
          updateServicePrice={updateServicePrice}
        />
      </div>
    </div>
  )

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='d-flex justify-content-between flex-wrap'>
            <div style={{flex: 1}}>
              <div className='w-100 card'>
                <div className='card-header py-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>
                      {i18n.t('hospitality_experiences.edit')}
                    </span>
                    <BreadCrumbs />
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      {i18n.t('hospitality_experiences.edit_subtitle')}
                    </span>
                  </h3>
                </div>
                <div className='card-body'>
                  <HospitalityTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={[
                      {
                        id: 'basic',
                        label: i18n.t('hospitality_experiences.tab_basic'),
                        icon: 'fas fa-info-circle',
                        content: renderBasicTab(),
                        hasError: false,
                      },
                      {
                        id: 'details',
                        label: i18n.t('hospitality_experiences.tab_details'),
                        icon: 'fas fa-cog',
                        content: renderDetailsTab(),
                        hasError: false,
                      },
                      {
                        id: 'services',
                        label: 'Serviços Adicionais',
                        icon: 'fas fa-cogs',
                        content: renderAdditionalServicesTab(),
                        hasError: false,
                      },
                    ]}
                  />
                  <div className='d-flex justify-content-end mt-5'>
                    <Button
                      text={i18n.t('buttons.cancel')}
                      color='danger'
                      size='small'
                      click={() => navigate('/hospitality/hotel-experiences')}
                      btnClass='me-3'
                    />
                    <Button
                      size='small'
                      text={i18n.t('hospitality_experiences.btn_update')}
                      color='primary'
                      click={updateExperience}
                      icon='fas fa-save'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const UpdateHotelExperience: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('general.dashboard'), path: 'dashboard', isActive: true},
          {
            title: i18n.t('hospitality_experiences.list_title'),
            path: 'hospitality/hotel-experiences',
            isActive: true,
          },
          {
            title: i18n.t('hospitality_experiences.edit_title'),
            path: 'hospitality/hotel-experiences/editar',
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_experiences.edit_title')}
      </PageTitle>
      <UpdateHotelExperienceContent />
    </>
  )
}

export {UpdateHotelExperience}
